import React, { useEffect, useRef, useState } from 'react';
import { TopBar } from '../../components/TopBar/TopBar';
import { Project } from '../../shared/models/Project';
import { DonationService } from '../../services/DonationService';
import * as WechatAuthService from '../../services/WechatAuthService';
import { getWechatNickname, getWechatAvatar } from '../../services/WechatAuthService';
import { chooseWXPay } from '../../services/WechatPayService';
import { useOrganization } from '../../contexts/OrganizationContext';
import { showError, logDebug, logError, logInfo } from '../../utils/errorHandler';
import { DonorProfileService } from '../../services/DonorProfileService';
import { DonationSuccessIcon } from '../../components/Icons/DonationSuccessIcon';
import './DonationConfirmView.css';

interface Props {
  project: Project;
  amount: number;
  recurring: boolean;
  anonymous: boolean;
  teamId?: string;
  onBack: () => void;
  onFinish: () => void;
  /** 支付成功结果页点击「查看捐赠证书」时回调，传入当前捐赠单 id 用于打开证书页 */
  onViewCertificate?: (donationId: string) => void;
}

export const DonationConfirmView: React.FC<Props> = ({ project, amount, recurring, anonymous, teamId, onBack, onFinish, onViewCertificate }) => {
  const { organization } = useOrganization();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [stage, setStage] = useState<'form' | 'processing' | 'result'>('form');
  const [processingStep, setProcessingStep] = useState(0);
  /** 支付成功进入结果页时保存的捐赠单 id，用于「查看捐赠证书」 */
  const [resultDonationId, setResultDonationId] = useState<string | null>(null);
  const timer1 = useRef<NodeJS.Timeout | null>(null);
  const hasClearedOpenIdRef = useRef(false); // 标记是否已清除 openId
  const isProcessingRef = useRef(false); // 标记是否正在处理支付流程
  
  // 组件挂载时，检查是否需要重新授权
  // 如果 URL 中有 openId 参数（从入口域名回调回来的），说明是刚授权回来的，不需要清除
  // 否则，清除旧的 openId，强制重新授权（使用公募机构的 AppID）
  // 注意：在支付流程中不应该重新授权，避免跳转
  useEffect(() => {
    // 如果正在处理支付流程，不执行授权检查
    if (isProcessingRef.current) {
      logInfo('[DonationConfirmView] Payment processing, skipping auth check');
      return;
    }
    
    if (WechatAuthService.isWechat() && project.id && !hasClearedOpenIdRef.current) {
      // 检查 URL 中是否有 openId 参数（从入口域名回调回来的）
      const urlParams = new URLSearchParams(window.location.search);
      const openIdFromEntry = urlParams.get('openId');
      
      // 检查是否已有 openId（从 localStorage）
      const existingOpenId = WechatAuthService.getOpenId();
      
      if (openIdFromEntry) {
        // 有 openId 参数，说明是刚授权回来的，不需要清除
        logInfo('[DonationConfirmView] OpenId from entry domain callback, no need to clear', {
          projectId: project.id
        });
        hasClearedOpenIdRef.current = true;
      } else if (existingOpenId) {
        // 已有 openId，不需要重新授权
        logInfo('[DonationConfirmView] OpenId already exists, no need to re-auth', {
          projectId: project.id,
          hasOpenId: !!existingOpenId
        });
        hasClearedOpenIdRef.current = true;
      } else {
        // 没有 openId，需要授权（但只在首次进入页面时，不在支付流程中）
        logInfo('[DonationConfirmView] No openId found, redirecting to auth', {
          projectId: project.id
        });
        hasClearedOpenIdRef.current = true;
        // 重新授权，传递 projectId，后端会使用公募机构的 AppID
        const appId = organization?.wechatAppId;
        if (appId) {
          logInfo('[DonationConfirmView] Redirecting to auth with projectId', {
            appId,
            projectId: project.id
          });
          WechatAuthService.redirectToAuth(appId, project.id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次
  const timer2 = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 使用 console.log 确保即使 logInfo 出错也能看到
    console.log('[DonationConfirmView] Component mounted (console.log)', {
      projectId: project.id,
      projectTitle: project.title,
      amount,
      recurring,
      anonymous,
      hasOrganization: !!organization,
      orgId: organization?.id,
      timestamp: new Date().toISOString()
    });
    
    logInfo('[DonationConfirmView] Component mounted', {
      projectId: project.id,
      projectTitle: project.title,
      amount,
      recurring,
      anonymous,
      hasOrganization: !!organization,
      orgId: organization?.id,
      timestamp: new Date().toISOString()
    });

    // 恢复用户输入的姓名和电话（授权回调后）
    const savedDonorInfo = localStorage.getItem('pending_donor_info');
    if (savedDonorInfo) {
      try {
        const donorInfo = JSON.parse(savedDonorInfo);
        // 验证项目ID是否匹配
        if (donorInfo.projectId === project.id) {
          logInfo('[DonationConfirmView] Restoring donor info from localStorage', {
            hasName: !!donorInfo.name,
            hasPhone: !!donorInfo.phone
          });
          if (donorInfo.name) {
            setName(donorInfo.name);
          }
          if (donorInfo.phone) {
            setPhone(donorInfo.phone);
          }
        } else {
          // 项目ID不匹配，清除保存的信息
          logInfo('[DonationConfirmView] Project ID mismatch, clearing saved donor info', {
            savedProjectId: donorInfo.projectId,
            currentProjectId: project.id
          });
          localStorage.removeItem('pending_donor_info');
        }
      } catch (error) {
        logError('[DonationConfirmView] Failed to restore donor info', error);
        localStorage.removeItem('pending_donor_info');
      }
    } else {
      // 如果没有保存的信息，尝试从用户信息表加载
      const openId = WechatAuthService.getOpenId();
      if (openId) {
        DonorProfileService.getProfile(openId).then(profile => {
          if (profile) {
            if (profile.name) {
              setName(profile.name);
            }
            if (profile.phone) {
              setPhone(profile.phone);
            }
            logInfo('[DonationConfirmView] Loaded user profile', {
              hasName: !!profile.name,
              hasPhone: !!profile.phone
            });
          }
        }).catch(error => {
          logError('[DonationConfirmView] Failed to load user profile', error);
        });
      }
    }
  }, [project.id]);

  // 清理定时器的 useEffect（依赖 stage 和 processingStep）
  useEffect(() => {
    return () => {
      // 这只是清理函数，不是组件卸载
      logInfo('[DonationConfirmView] Cleanup function called (stage/step changed)', {
        stage,
        processingStep,
        hasTimer1: !!timer1.current,
        hasTimer2: !!timer2.current
      });
      if (timer1.current) clearTimeout(timer1.current);
      if (timer2.current) clearTimeout(timer2.current);
    };
  }, [stage, processingStep]);

  // 真正的组件卸载监听（无依赖，只在组件卸载时执行）
  useEffect(() => {
    return () => {
      console.log('[DonationConfirmView] Component REALLY unmounting (console.log)', {
        stage,
        processingStep,
        timestamp: new Date().toISOString()
      });
      logInfo('[DonationConfirmView] Component REALLY unmounting', {
        stage,
        processingStep,
        hasTimer1: !!timer1.current,
        hasTimer2: !!timer2.current
      });
    };
  }, []);

  const handleSubmit = async () => {
    // 使用 console.log 确保即使 logInfo 出错也能看到
    console.log('[DonationConfirmView] handleSubmit called (console.log)', {
      name,
      phone,
      amount,
      recurring,
      anonymous,
      projectId: project.id,
      stage,
      processingStep,
      timestamp: new Date().toISOString()
    });
    
    logInfo('[DonationConfirmView] handleSubmit called', {
      name,
      phone,
      amount,
      recurring,
      anonymous,
      projectId: project.id,
      stage,
      processingStep,
      timestamp: new Date().toISOString()
    });

    if (!name.trim()) {
      logError('[DonationConfirmView] Form validation failed: name is empty');
      showError('请填写姓名', 'DonationConfirmView.handleSubmit');
      return;
    }
    if (!/^1\d{10}$/.test(phone.trim())) {
      logError('[DonationConfirmView] Form validation failed: invalid phone', { phone });
      showError('请填写有效的手机号', 'DonationConfirmView.handleSubmit');
      return;
    }

    logInfo('[DonationConfirmView] Form validation passed', {
      name: name.trim(),
      phone: phone.trim(),
      amount,
      recurring
    });

    // 标记开始处理支付流程，避免授权逻辑干扰
    isProcessingRef.current = true;

    try {
      logInfo('[DonationConfirmView] Setting stage to processing');
      setStage('processing');
      setProcessingStep(1);
      logInfo('[DonationConfirmView] Stage set to processing', {
        stage: 'processing',
        step: 1
      });
      logInfo('[DonationConfirmView] Step 1: Getting openId');

      // 1. 获取 openId（方案 A：授权已在进入页面前完成，这里只需要获取）
      let openId: string | null = null;
      if (WechatAuthService.isWechat()) {
        logInfo('[DonationConfirmView] In WeChat environment, getting openId');
        openId = WechatAuthService.getOpenId();
        logInfo('[DonationConfirmView] OpenId from storage', { openId, hasOpenId: !!openId });
        
        if (!openId) {
          // 如果没有 openId，说明授权流程有问题，不应该进入这个页面
          const error = new Error('未获取到微信授权信息，请重新尝试捐赠');
          logError('[DonationConfirmView] No openId found, should have been authorized before entering this page', {
            hasOrg: !!organization,
            hasAppId: !!organization?.wechatAppId
          });
          showError(error, 'DonationConfirmView.handleSubmit');
          setStage('form');
          setProcessingStep(0);
          return;
        }
      } else {
        logInfo('[DonationConfirmView] Not in WeChat environment, skipping openId');
      }

      // 2. 更新用户信息（如果提供了 openId）
      if (openId && name.trim() && phone.trim()) {
        try {
          await DonorProfileService.updateProfile(openId, name.trim(), phone.trim());
          logInfo('[DonationConfirmView] User profile updated before donation');
        } catch (error) {
          logError('[DonationConfirmView] Failed to update user profile', error);
          // 不影响捐赠流程，继续执行
        }
      }

      // 3. 创建捐赠订单
      logInfo('[DonationConfirmView] Step 2: Creating donation order', {
        projectId: project.id,
        amount,
        donationType: recurring ? 'MONTHLY' : 'ONE_TIME',
        hasOpenId: !!openId
      });
      setProcessingStep(2);
      
      logInfo('[DonationConfirmView] About to call createDonation', {
        projectId: project.id,
        amount,
        donationType: recurring ? 'MONTHLY' : 'ONE_TIME',
        hasOpenId: !!openId,
        donorName: name,
        donorPhone: phone
      });
      
      // 获取微信用户信息（如果已授权）
      // 注意：无论是否匿名，都应该保存昵称和头像，isAnonymous 只用于控制展示
      const wechatNickname = getWechatNickname();
      const wechatAvatar = getWechatAvatar();
      
      const donation = await DonationService.createDonation({
        projectId: project.id,
        teamId,
        amount,
        donationType: recurring ? 'MONTHLY' : 'ONE_TIME',
        donorName: name,
        donorPhone: phone,
        donorOpenId: openId || undefined,
        donorNickname: wechatNickname || undefined, // 无论是否匿名都保存昵称
        donorAvatar: wechatAvatar || undefined,     // 无论是否匿名都保存头像
        isAnonymous: anonymous,
        monthlyDeductionDate: recurring ? 11 : undefined, // 默认每月11号扣款
      });

      logInfo('[DonationConfirmView] createDonation response received', {
        hasDonation: !!donation,
        donationId: donation?.id,
        status: donation?.status,
        hasPayment: !!donation?.payment,
        donationOrderNo: donation?.donationOrderNo,
        paymentKeys: donation?.payment ? Object.keys(donation.payment) : []
      });

      // 3. 处理支付流程
      if (!recurring) {
        // 单次捐赠：需要调起支付
        logInfo('[DonationConfirmView] Step 3: Processing one-time donation payment', {
          hasPayment: !!donation.payment,
          hasOpenId: !!openId,
          donationId: donation.id,
          donationOrderNo: donation.donationOrderNo
        });
        
        // 检查支付参数
        if (!openId) {
          const error = new Error('单次捐赠需要微信授权，但未获取到 openId\n请确保在微信环境中访问，并完成授权');
          logError('[DonationConfirmView] OpenId missing for one-time donation', {
            isWechat: WechatAuthService.isWechat(),
            hasOrg: !!organization,
            hasAppId: !!organization?.wechatAppId
          });
          showError(error, 'DonationConfirmView.handleSubmit.payment');
          setStage('form');
          setProcessingStep(0);
          return;
        }
        
        if (!donation.payment) {
          const error = new Error(`单次捐赠需要支付参数，但后端未返回\n捐赠订单号: ${donation.donationOrderNo}\n可能原因：支付配置缺失或支付创建失败`);
          logError('[DonationConfirmView] Payment params missing for one-time donation', {
            donationId: donation.id,
            donationOrderNo: donation.donationOrderNo,
            status: donation.status,
            hasOpenId: !!openId
          });
          showError(error, 'DonationConfirmView.handleSubmit.payment');
          setStage('form');
          setProcessingStep(0);
          return;
        }
        
        // 验证支付参数完整性
        const requiredFields = ['timeStamp', 'nonceStr', 'package', 'signType', 'paySign'];
        const missingFields = requiredFields.filter(field => !donation.payment?.[field as keyof typeof donation.payment]);
        if (missingFields.length > 0) {
          const error = new Error(`支付参数不完整，缺少字段: ${missingFields.join(', ')}\n支付参数: ${JSON.stringify(donation.payment, null, 2)}`);
          logError('[DonationConfirmView] Payment params incomplete', {
            missingFields,
            payment: donation.payment
          });
          showError(error, 'DonationConfirmView.handleSubmit.payment');
          setStage('form');
          setProcessingStep(0);
          return;
        }
        
        // 调起支付
        try {
          logInfo('[DonationConfirmView] Calling WeChat Pay', {
            hasPayment: !!donation.payment,
            hasOpenId: !!openId,
            paymentFields: Object.keys(donation.payment || {}),
            projectId: project.id
          });
          await chooseWXPay(donation.payment, project.id);
          // 支付成功
          logInfo('[DonationConfirmView] Payment success');
          // 清除保存的用户输入信息
          localStorage.removeItem('pending_donor_info');
          setResultDonationId(donation.id);
          setStage('result');
          setProcessingStep(0);
        } catch (payError: any) {
          // 支付失败或取消
          const errorMessage = payError?.message || payError?.errMsg || String(payError);
          const errMsg = payError?.errMsg ?? '';

          // 判断是否是用户取消支付（只打 log，不弹窗）
          const isUserCancel =
            errorMessage.includes('用户取消支付') ||
            errorMessage.includes('用户取消') ||
            errorMessage.toLowerCase().includes('cancel') ||
            errMsg.toLowerCase().includes('cancel');

          if (isUserCancel) {
            logInfo('[DonationConfirmView] Payment cancelled by user - 微信支付', {
              message: errorMessage,
              errMsg,
            });
          } else {
            // 支付失败，显示错误
            const errorString = JSON.stringify(payError, null, 2);
            const errorDetails = {
              error: errorMessage,
              errMsg: payError?.errMsg,
              message: payError?.message,
              errorType: payError?.constructor?.name,
              errorKeys: payError ? Object.keys(payError) : [],
              errorString: errorString, // 完整的错误对象字符串
              currentPageUrl: window.location.href,
              currentPageUrlWithoutHash: window.location.href.split('#')[0],
              currentHost: window.location.host,
              currentPathname: window.location.pathname,
              currentSearch: window.location.search,
              // 检查是否包含 "not registered" 相关错误
              hasNotRegisteredError: errorString.toLowerCase().includes('not registered') || 
                                     errorString.toLowerCase().includes('未注册') ||
                                     errorMessage.toLowerCase().includes('not registered') ||
                                     errorMessage.toLowerCase().includes('未注册')
            };
            logError('[DonationConfirmView] Payment failed - 微信支付', errorDetails);
            showError(payError, 'DonationConfirmView.handleSubmit.payment');
          }
          
          setStage('form');
          setProcessingStep(0);
          isProcessingRef.current = false; // 重置处理标记
        }
      } else {
        // 月捐：不需要立即支付，直接显示结果
        logInfo('[DonationConfirmView] Monthly donation, showing result', {
          recurring,
          donationId: donation.id,
          donationOrderNo: donation.donationOrderNo,
          status: donation.status
        });
        setResultDonationId(donation.id);
        setStage('result');
        setProcessingStep(0);
        isProcessingRef.current = false; // 重置处理标记
      }
    } catch (error: any) {
      // 错误处理
      logError('[DonationConfirmView] handleSubmit error caught', {
        errorType: error?.constructor?.name,
        errorMessage: error?.message,
        errorStack: error?.stack?.split('\n').slice(0, 5).join('\n'),
        hasResponseData: !!error?.responseData,
        responseCode: error?.responseCode,
        responseMessage: error?.responseMessage,
        hasDonation: !!error?.donation,
        currentStage: stage,
        currentStep: processingStep
      });
      
      // 如果错误中包含订单记录（支付失败但订单已创建的情况）
      if (error?.donation) {
        logInfo('[DonationConfirmView] Donation record found in error', {
          donationId: error.donation.id,
          status: error.donation.status,
          donationOrderNo: error.donation.donationOrderNo,
          errorMessage: error.message
        });
        // 订单记录已保存在数据库中，状态为 FAILED
        // 用户可以在"我的捐赠"中查看这个失败的订单
      }
      
      logInfo('[DonationConfirmView] Showing error and resetting stage', {
        errorMessage: error?.message,
        willSetStageTo: 'form',
        willSetStepTo: 0
      });
      showError(error, 'DonationConfirmView.handleSubmit');
      setStage('form');
      setProcessingStep(0);
      isProcessingRef.current = false; // 重置处理标记
    }
  };

  const isStopped = project.status === 'STOPPED';
  const cover = project.bigImageUrl || project.coverImageUrl || project.coverThumbUrl || '/placeholder.svg';

  // 根据金额匹配 fundraisingTiers 中的 description
  const getTierDescription = (): string => {
    if (project.fundraisingTiers && project.fundraisingTiers.length > 0) {
      const matchedTier = project.fundraisingTiers.find(tier => tier.amount === amount);
      if (matchedTier && matchedTier.description) {
        return matchedTier.description;
      }
    }
    // 如果没有找到匹配的 tier，返回项目默认描述
    return project.description || '';
  };

  if (stage === 'result') {
    return (
      <div className="dc-page dc-page-result">
        <TopBar title="捐赠成功" showBack onBack={onFinish} />
        <div className="dc-result-body">
          <div className="dc-result-icon">
            <DonationSuccessIcon className="dc-result-icon-svg" />
          </div>
          <div className="dc-result-user">「 爱心用户 」</div>
          <div className="dc-result-thanks">感谢您支持了</div>
          <div className="dc-result-desc">{project.title || '公益项目'}</div>
          <div className="dc-result-amount">
            <span className="price">{amount.toFixed(2)}</span> 元
          </div>
          <div className="dc-result-actions">
            <button
              className="dc-btn dc-btn-primary"
              onClick={() => {
                if (resultDonationId && onViewCertificate) {
                  onViewCertificate(resultDonationId);
                } else {
                  alert('暂无捐赠证书，后续可接入');
                }
              }}
            >
              查看捐赠证书
            </button>
            <button className="dc-btn dc-btn-ghost" onClick={onFinish}>
              返回项目
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dc-page">
      <TopBar 
        title="确认支付" 
        showBack 
        onBack={() => {
          logInfo('[DonationConfirmView] TopBar back button clicked', { stage, processingStep });
          onBack();
        }} 
      />

      <div className="dc-body">
        <div className="dc-card dc-project">
          <img
            src={cover}
            alt={project.title}
            onError={(e) => {
              e.currentTarget.src = '/placeholder.svg';
            }}
          />
          <div className="dc-project-info">
            <div className="dc-project-title">{project.title}</div>
            <div className="dc-project-desc">{getTierDescription()}</div>
            <div className="dc-amount">
              {amount} 元{recurring ? '（每月一捐）' : '（单次捐）'} {anonymous ? '· 匿名' : ''}
            </div>
          </div>
        </div>

        {stage === 'form' && (
          <div className="dc-card">
            <div className="dc-section-title">捐赠人信息</div>
            <div className="dc-form-item">
              <label>姓名</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="请输入" />
            </div>
            <div className="dc-form-item">
              <label>手机号</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="请输入" />
            </div>
            <div className="dc-tip">根据过去记录填写，以为你自动填写</div>
          </div>
        )}
      </div>

      <div className="dc-footer">
        <button
          className="dc-btn dc-btn-primary"
          disabled={isStopped || stage !== 'form'}
          onClick={() => {
            // 立即打印日志，确保即使后续出错也能看到
            console.log('[DonationConfirmView] Pay button clicked (console.log)', {
              stage,
              isStopped,
              disabled: isStopped || stage !== 'form',
              timestamp: new Date().toISOString()
            });
            logInfo('[DonationConfirmView] Pay button clicked', {
              stage,
              isStopped,
              disabled: isStopped || stage !== 'form',
              timestamp: new Date().toISOString()
            });
            
            try {
              if (stage === 'form') {
                console.log('[DonationConfirmView] Calling handleSubmit (console.log)');
                handleSubmit();
              } else {
                console.log('[DonationConfirmView] Calling onFinish (console.log)');
                onFinish();
              }
            } catch (error: any) {
              console.error('[DonationConfirmView] Error in button onClick (console.error)', error);
              logError('[DonationConfirmView] Error in button onClick', error);
              showError(error, 'DonationConfirmView.button.onClick');
            }
          }}
        >
          去支付
        </button>
      </div>
    </div>
  );
};


